/**
 * AppointmentsTab - Displays call bookings made by leads
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Phone, Calendar, Clock, User, Mail, MessageSquare, 
  CheckCircle, XCircle, Loader2, RefreshCw, Trash2, Crown
} from 'lucide-react';
import { Button } from '../ui/button';
import { getAuthToken } from '../../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AppointmentsTab = ({ isDark, onCountUpdate }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, confirmed, cancelled

  const fetchBookings = useCallback(async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/dealer/call-bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(res.data || []);
      
      // Update pending count in parent
      if (onCountUpdate) {
        const pendingCount = (res.data || []).filter(b => b.status === 'pending').length;
        onCountUpdate(pendingCount);
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  }, [onCountUpdate]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      const token = getAuthToken();
      await axios.patch(`${API}/dealer/call-booking/${bookingId}/status`, null, {
        params: { status: newStatus },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Appointment ${newStatus}`);
      fetchBookings();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Delete this appointment permanently? This cannot be undone.')) return;
    try {
      const token = getAuthToken();
      await axios.delete(`${API}/dealer/call-booking/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Appointment deleted');
      setBookings((prev) => prev.filter((b) => b.id !== bookingId));
    } catch (err) {
      toast.error('Failed to delete appointment');
    }
  };

  // Priority weight so VIP + urgent bookings float to the top of every filter
  // view. VIP bookings always sort above non-VIP; within VIP, urgent (concierge)
  // beats high (buying plan) beats normal.
  const PRIORITY_WEIGHT = { urgent: 3, high: 2, normal: 1 };
  const priorityScore = (b) => {
    let score = 0;
    if (b.is_vip) score += 100;
    score += PRIORITY_WEIGHT[b.priority] || 0;
    // Pending sorts above confirmed/completed within the same priority tier
    if (b.status === 'pending') score += 10;
    return score;
  };

  const filteredBookings = bookings
    .filter(b => {
      if (filter === 'all') return true;
      if (filter === 'vip') return !!b.is_vip;
      return b.status === filter;
    })
    .sort((a, b) => {
      const diff = priorityScore(b) - priorityScore(a);
      if (diff !== 0) return diff;
      // Newest first as tiebreaker
      return (b.created_at || '').localeCompare(a.created_at || '');
    });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      weekday: 'short', month: 'short', day: 'numeric' 
    });
  };

  const statusColors = {
    pending: 'bg-amber-100 text-amber-700 border-amber-200',
    confirmed: 'bg-green-100 text-green-700 border-green-200',
    cancelled: 'bg-red-100 text-red-700 border-red-200',
    completed: 'bg-blue-100 text-blue-700 border-blue-200'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="appointments-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-xl ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
            <Phone className="text-blue-600" size={28} />
          </div>
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Appointments
            </h1>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Call requests from your leads
            </p>
          </div>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchBookings}
          className="flex items-center gap-2"
        >
          <RefreshCw size={16} />
          Refresh
        </Button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'vip', 'pending', 'confirmed', 'completed', 'cancelled'].map(f => {
          const isVipFilter = f === 'vip';
          const vipCount = bookings.filter(b => b.is_vip).length;
          const pendingCount = bookings.filter(b => b.status === 'pending').length;
          const activeClass = isVipFilter
            ? 'bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white'
            : 'bg-blue-600 text-white';
          return (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all capitalize inline-flex items-center gap-1.5 ${
                filter === f
                  ? activeClass
                  : isDark
                    ? 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`filter-${f}`}
            >
              {isVipFilter && <Crown className="w-3.5 h-3.5" />}
              {f}
              {isVipFilter && vipCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-fuchsia-500 text-white text-xs rounded-full">
                  {vipCount}
                </span>
              )}
              {f === 'pending' && pendingCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {pendingCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Bookings List */}
      {filteredBookings.length === 0 ? (
        <div className={`text-center py-12 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <Phone size={48} className={`mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
          <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>
            {filter === 'all' ? 'No appointment requests yet' : `No ${filter} appointments`}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredBookings.map(booking => (
            <div 
              key={booking.id}
              className={`rounded-xl border p-5 relative ${
                booking.is_vip
                  ? 'border-fuchsia-400/50 bg-gradient-to-br from-fuchsia-500/10 via-violet-500/5 to-transparent ring-1 ring-fuchsia-400/20'
                  : isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}
              data-testid={`booking-${booking.id}`}
            >
              {booking.is_vip && (
                <div className="absolute -top-2 -right-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white text-[10px] font-black uppercase tracking-widest shadow-lg" data-testid={`booking-vip-badge-${booking.id}`}>
                  <Crown className="w-3 h-3" />
                  {booking.vip_product_id === 'vip_concierge' ? 'VIP Concierge' : 'VIP'}
                </div>
              )}
              <div className="flex items-start justify-between">
                {/* Left - Contact Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      booking.is_vip
                        ? 'bg-fuchsia-500/20 border border-fuchsia-400/40'
                        : isDark ? 'bg-blue-500/20' : 'bg-blue-100'
                    }`}>
                      {booking.is_vip
                        ? <Crown className="text-fuchsia-300" size={22} />
                        : <User className="text-blue-600" size={24} />}
                    </div>
                    <div>
                      <h3 className={`font-bold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {booking.name}
                      </h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusColors[booking.status]}`}>
                        {booking.status}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Mail size={14} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                      <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{booking.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone size={14} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                      <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{booking.phone || 'Not provided'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar size={14} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                      <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{formatDate(booking.preferred_date)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock size={14} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                      <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{booking.preferred_time || 'Any time'}</span>
                    </div>
                  </div>

                  {booking.notes && (
                    <div className={`mt-3 p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <div className="flex items-start gap-2">
                        <MessageSquare size={14} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{booking.notes}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right - Actions */}
                <div className="flex flex-col gap-2 ml-4">
                  {booking.status === 'pending' && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`confirm-${booking.id}`}
                      >
                        <CheckCircle size={14} className="mr-1" />
                        Confirm
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                        className="text-red-600 hover:bg-red-50 border-red-200"
                        data-testid={`cancel-${booking.id}`}
                      >
                        <XCircle size={14} className="mr-1" />
                        Cancel
                      </Button>
                    </>
                  )}
                  {booking.status === 'confirmed' && (
                    <Button
                      size="sm"
                      onClick={() => updateBookingStatus(booking.id, 'completed')}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid={`complete-${booking.id}`}
                    >
                      <CheckCircle size={14} className="mr-1" />
                      Mark Complete
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => deleteBooking(booking.id)}
                    className={`${isDark ? 'text-red-400 hover:bg-red-500/10 border-red-500/40' : 'text-red-600 hover:bg-red-50 border-red-200'}`}
                    data-testid={`delete-${booking.id}`}
                    title="Delete appointment"
                  >
                    <Trash2 size={14} className="mr-1" />
                    Delete
                  </Button>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {new Date(booking.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AppointmentsTab;
